import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector, RootState } from '../../../../state/state';
import { selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2';
import { toast } from 'react-toastify';
import { collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp, addDoc } from "firebase/firestore";
import { db } from "../../../../Config/firebase";
import { Menu, Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { TextField, FormControlLabel, Switch, Tooltip } from '@mui/material';
import { Slot } from '../../types/SubscriptionType';

interface SlotAssignment {
    studentId: string;
    studentName: string;
    assignedAt: any;
    status: string;
}

interface SlotFormData {
    name: string;
    dayOfWeek: string[];
    startTime: string;
    durationMinutes: number;
    maxCapacity: number;
    isActive: boolean;
    markupAllowed: boolean;
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const WEEKENDS = ['Saturday', 'Sunday'];

// Helper function to format time
const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
};

export default function SlotsPage() {
    const navigate = useNavigate();
    const { Subscriptionid } = useParams();
    const providerId = useAppSelector(selectEffectiveUID);
    
    // State declarations
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>('');
    const [slotAssignments, setSlotAssignments] = useState<{ [key: string]: SlotAssignment[] }>({});
    const [editingSlot, setEditingSlot] = useState<string | null>(null);
    const [formData, setFormData] = useState<SlotFormData>({
        name: '',
        dayOfWeek: [],
        startTime: '09:00',
        durationMinutes: 60,
        maxCapacity: 10,
        isActive: true,
        markupAllowed: false
    });
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
    
    // Add state for new slot form
    const [showAddForm, setShowAddForm] = useState(false);

    // Add new state for tracking which slot we're assigning to
    const [assigningSlot, setAssigningSlot] = useState<string | null>(null);

    // Add state for slots
    const [slots, setSlots] = useState<Slot[]>([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [slotsError, setSlotsError] = useState<string | null>(null);
    const [isUpdatingAssignments, setIsUpdatingAssignments] = useState(false);

    // Redux selectors
    const allSubscriptions = useAppSelector((state: RootState) => state.subscriptionTableSlice.data) || [];

    // Effect to handle initial subscription selection and data loading
    useEffect(() => {
        if (!Subscriptionid && allSubscriptions.length > 0) {
            // If no subscription ID in URL but we have subscriptions, redirect to first one
            const firstId = allSubscriptions[0].id;
            setSelectedSubscriptionId(firstId);
            navigate(`/slots/${firstId}`, { replace: true });
        } else if (Subscriptionid) {
            // If we have a subscription ID in URL, use that
            setSelectedSubscriptionId(Subscriptionid);
        }
    }, [Subscriptionid, allSubscriptions, navigate]);

    // Effect to fetch slots for the selected subscription
    useEffect(() => {
        const fetchSlots = async () => {
            if (!providerId || !selectedSubscriptionId) return;
            
            setSlotsLoading(true);
            setSlotsError(null);
            
            try {
                const slotsRef = collection(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots`);
                const slotsSnapshot = await getDocs(slotsRef);
                const slotsData: Slot[] = [];
                
                slotsSnapshot.forEach((doc) => {
                    slotsData.push({
                        id: doc.id,
                        ...doc.data()
                    } as Slot);
                });
                
                setSlots(slotsData);
            } catch (error) {
                console.error('Error fetching slots:', error);
                setSlotsError('Failed to load slots');
            } finally {
                setSlotsLoading(false);
            }
        };

        fetchSlots();
    }, [providerId, selectedSubscriptionId]);

    // Effect to fetch slot assignments
    useEffect(() => {
        const fetchAssignments = async () => {
            if (!providerId || !selectedSubscriptionId || !slots.length || isUpdatingAssignments) {
                console.log('Skipping assignment fetch:', { providerId, selectedSubscriptionId, slotsLength: slots.length, isUpdatingAssignments });
                return;
            }
            
            try {
                console.log('Fetching assignments for slots:', slots.map(s => s.id));
                const newAssignments: { [key: string]: SlotAssignment[] } = {};
                
                for (const slot of slots) {
                    const assignmentsRef = collection(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots/${slot.id}/Assignments`);
                    const assignmentsSnap = await getDocs(assignmentsRef);
                    
                    newAssignments[slot.id] = assignmentsSnap.docs.map(doc => ({
                        studentId: doc.id,
                        ...doc.data() as Omit<SlotAssignment, 'studentId'>
                    }));
                }
                
                console.log('Fetched assignments:', newAssignments);
                setSlotAssignments(newAssignments);
            } catch (error) {
                console.error('Error fetching assignments:', error);
                toast.error('Failed to load slot assignments');
            }
        };

        fetchAssignments();
    }, [providerId, selectedSubscriptionId, slots, isUpdatingAssignments]);

    // Get live subscriptions for the current subscription
    const allLiveSubscriptions = useAppSelector((state: RootState) => state.LiveSubscriptionSlice.data) || [];
    const filteredLiveSubscriptions = allLiveSubscriptions.filter(sub => 
        sub.subscriptionPlanRefId === selectedSubscriptionId && 
        sub.status !== 'Inactive' && 
        sub.status !== 'Terminated'
    );

    // Handle subscription change
    const handleSubscriptionChange = (subscriptionId: string) => {
        console.log('Changing subscription to:', subscriptionId);
        setSelectedSubscriptionId(subscriptionId);
        navigate(`/slots/${subscriptionId}`);
    };

    // Update form data state to include name and markup
    const handleInputChange = (field: keyof SlotFormData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDayChange = (day: string) => {
        setFormData((prev: SlotFormData) => {
            const currentDays = Array.isArray(prev.dayOfWeek) ? prev.dayOfWeek : [];
            const newDays = currentDays.includes(day)
                ? currentDays.filter((d: string) => d !== day)
                : [...currentDays, day];
            return { ...prev, dayOfWeek: newDays };
        });
    };

    const handleSelectWeekdays = () => {
        setFormData((prev: SlotFormData) => ({
            ...prev,
            dayOfWeek: WEEKDAYS
        }));
    };

    const handleSelectWeekends = () => {
        setFormData((prev: SlotFormData) => ({
            ...prev,
            dayOfWeek: WEEKENDS
        }));
    };

    const handleClearAllDays = () => {
        setFormData((prev: SlotFormData) => ({
            ...prev,
            dayOfWeek: []
        }));
    };

    // Update handleCreateSlot to include new fields
    const handleCreateSlot = async () => {
        if (!providerId || !selectedSubscriptionId) return;
        
        if (formData.dayOfWeek.length === 0) {
            toast.error('Please select at least one day');
            return;
        }

        if (!formData.name.trim()) {
            toast.error('Please enter a slot name');
            return;
        }

        try {
            await addDoc(collection(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots`), {
                name: formData.name.trim(),
                dayOfWeek: formData.dayOfWeek,
                startTime: formData.startTime,
                durationMinutes: formData.durationMinutes,
                maxCapacity: formData.maxCapacity,
                isActive: formData.isActive,
                markupAllowed: formData.markupAllowed,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
            setShowAddForm(false);
            setFormData({
                name: '',
                dayOfWeek: [],
                startTime: '09:00',
                durationMinutes: 60,
                maxCapacity: 10,
                isActive: true,
                markupAllowed: false
            });
            
            // Refresh slots data
            const slotsRef = collection(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots`);
            const slotsSnapshot = await getDocs(slotsRef);
            const slotsData: Slot[] = [];
            slotsSnapshot.forEach((doc) => {
                slotsData.push({
                    id: doc.id,
                    ...doc.data()
                } as Slot);
            });
            setSlots(slotsData);
            
            toast.success('Slot created successfully');
        } catch (error) {
            console.error('Error creating slot:', error);
            toast.error('Failed to create slot');
        }
    };

    // Update handleUpdateSlot to include new fields
    const handleUpdateSlot = async () => {
        if (!providerId || !selectedSubscriptionId || !editingSlot) return;

        if (formData.dayOfWeek.length === 0) {
            toast.error('Please select at least one day');
            return;
        }

        if (!formData.name.trim()) {
            toast.error('Please enter a slot name');
            return;
        }

        try {
            const slotRef = doc(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots/${editingSlot}`);
            await setDoc(slotRef, {
                name: formData.name.trim(),
                dayOfWeek: formData.dayOfWeek,
                startTime: formData.startTime,
                durationMinutes: formData.durationMinutes,
                maxCapacity: formData.maxCapacity,
                isActive: formData.isActive,
                markupAllowed: formData.markupAllowed,
                updatedAt: serverTimestamp()
            }, { merge: true });
            setEditingSlot(null);
            
            // Refresh slots data
            const slotsRef = collection(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots`);
            const slotsSnapshot = await getDocs(slotsRef);
            const slotsData: Slot[] = [];
            slotsSnapshot.forEach((doc) => {
                slotsData.push({
                    id: doc.id,
                    ...doc.data()
                } as Slot);
            });
            setSlots(slotsData);
            
            toast.success('Slot updated successfully');
        } catch (error) {
            console.error('Error updating slot:', error);
            toast.error('Failed to update slot');
        }
    };

    // Handle student assignment
    const handleAssignStudents = async () => {
        if (!assigningSlot || !providerId || !selectedSubscriptionId) return;
        
        try {
            const slot = slots.find(s => s.id === assigningSlot);
            if (!slot) {
                toast.error('Slot not found');
                return;
            }

            const currentAssignments = slotAssignments[assigningSlot] || [];
            const selectedStudentsArray = Array.from(selectedStudents);
            const studentsToAssign = filteredLiveSubscriptions.filter(sub => selectedStudents.has(sub.id));

            // Check capacity
            if (currentAssignments.length + selectedStudentsArray.length > slot.maxCapacity) {
                toast.error(`Cannot assign ${selectedStudentsArray.length} students. Slot capacity is ${slot.maxCapacity}`);
                return;
            }

            // Check for duplicates in this specific slot
            const duplicateStudents = studentsToAssign.filter(student => 
                currentAssignments.some(assignment => assignment.studentId === student.id)
            );

            if (duplicateStudents.length > 0) {
                toast.error(`Some students are already assigned to this slot: ${duplicateStudents.map(s => s.studentName).join(', ')}`);
                return;
            }

            // Create assignments
            const assignmentPromises = studentsToAssign.map(async student => {
                const assignmentRef = doc(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots/${assigningSlot}/Assignments/${student.id}`);
                await setDoc(assignmentRef, {
                    studentId: student.id,
                    studentName: student.studentName,
                    assignedAt: serverTimestamp(),
                    status: 'active'
                });
            });

            await Promise.all(assignmentPromises);
            
            // Refresh assignments data from Firestore
            const newAssignments: { [key: string]: SlotAssignment[] } = {};
            for (const slot of slots) {
                const assignmentsRef = collection(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots/${slot.id}/Assignments`);
                const assignmentsSnap = await getDocs(assignmentsRef);
                newAssignments[slot.id] = assignmentsSnap.docs.map(doc => ({
                    studentId: doc.id,
                    ...doc.data() as Omit<SlotAssignment, 'studentId'>
                }));
            }
            setSlotAssignments(newAssignments);

            setSelectedStudents(new Set());
            setIsAssignModalOpen(false);
            setAssigningSlot(null);
            toast.success('Students assigned successfully');
        } catch (error) {
            console.error('Error assigning students:', error);
            toast.error('Failed to assign students');
        }
    };

    // Handle removing a student from slot
    const handleRemoveStudent = async (studentId: string, slotId: string) => {
        if (!providerId || !selectedSubscriptionId) {
            console.error('Missing providerId or selectedSubscriptionId');
            return;
        }
        
        try {
            setIsUpdatingAssignments(true);
            console.log('Attempting to remove student:', { studentId, slotId });
            console.log('Current assignments for this slot:', slotAssignments[slotId]);

            // First, let's check what documents actually exist in Firestore
            const assignmentsRef = collection(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots/${slotId}/Assignments`);
            const assignmentsSnap = await getDocs(assignmentsRef);
            console.log('Actual documents in Firestore:', assignmentsSnap.docs.map(doc => ({ id: doc.id, data: doc.data() })));

            // Find the document that contains this studentId
            const targetDoc = assignmentsSnap.docs.find(doc => doc.data().studentId === studentId);
            
            if (targetDoc) {
                console.log('Found document to delete:', targetDoc.id);
                await deleteDoc(targetDoc.ref);
                console.log('Document deleted successfully');
                
                // Update local state immediately
                setSlotAssignments(prev => ({
                    ...prev,
                    [slotId]: prev[slotId]?.filter(a => a.studentId !== studentId) || []
                }));
                
                toast.success('Student removed from slot');
            } else {
                console.error('No document found with studentId:', studentId);
                toast.error('Assignment not found in database');
            }
        } catch (error) {
            console.error('Error removing student:', error);
            toast.error(`Failed to remove student: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsUpdatingAssignments(false);
        }
    };

    // Handle moving a student between slots
    const handleMoveStudent = async (studentId: string, fromSlotId: string, toSlotId: string) => {
        if (!providerId || !selectedSubscriptionId) return;
        
        try {
            // First check if we have the student data in our local state
            const studentData = slotAssignments[fromSlotId]?.find(a => a.studentId === studentId);
            if (!studentData) {
                toast.error('Student assignment data not found');
                return;
            }

            // Check target slot capacity
            const targetSlot = slots.find(s => s.id === toSlotId);
            if (!targetSlot) {
                toast.error('Target slot not found');
                return;
            }

            const currentAssignments = slotAssignments[toSlotId] || [];

            // Check if student is already in target slot
            if (currentAssignments.some(assignment => assignment.studentId === studentId)) {
                toast.error('Student is already assigned to the target slot');
                return;
            }

            if (currentAssignments.length >= targetSlot.maxCapacity) {
                toast.error('Target slot is at maximum capacity');
                return;
            }

            // Move student
            const sourceRef = doc(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots/${fromSlotId}/Assignments/${studentId}`);
            const targetRef = doc(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots/${toSlotId}/Assignments/${studentId}`);
            
            // Create new assignment in target slot
            await setDoc(targetRef, {
                studentId: studentData.studentId,
                studentName: studentData.studentName,
                assignedAt: serverTimestamp(),
                status: 'active'
            });

            // Remove from source slot
            await deleteDoc(sourceRef);

            // Refresh assignments data from Firestore
            const newAssignments: { [key: string]: SlotAssignment[] } = {};
            for (const slot of slots) {
                const assignmentsRef = collection(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots/${slot.id}/Assignments`);
                const assignmentsSnap = await getDocs(assignmentsRef);
                newAssignments[slot.id] = assignmentsSnap.docs.map(doc => ({
                    studentId: doc.id,
                    ...doc.data() as Omit<SlotAssignment, 'studentId'>
                }));
            }
            setSlotAssignments(newAssignments);

            toast.success('Student moved successfully');
        } catch (error) {
            console.error('Error moving student:', error);
            toast.error('Failed to move student');
        }
    };

    // Handle slot deletion
    const handleDeleteSlot = async (slotId: string) => {
        if (!providerId || !selectedSubscriptionId) return;

        const assignments = slotAssignments[slotId] || [];
        if (assignments.length > 0) {
            toast.error('Cannot delete slot with assigned students');
            return;
        }

        if (window.confirm('Are you sure you want to delete this slot?')) {
            try {
                const slotRef = doc(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots/${slotId}`);
                await deleteDoc(slotRef);
                
                // Refresh slots data
                const slotsRef = collection(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots`);
                const slotsSnapshot = await getDocs(slotsRef);
                const slotsData: Slot[] = [];
                slotsSnapshot.forEach((doc) => {
                    slotsData.push({
                        id: doc.id,
                        ...doc.data()
                    } as Slot);
                });
                setSlots(slotsData);
                
                // Also refresh assignments
                const newAssignments: { [key: string]: SlotAssignment[] } = {};
                for (const slot of slotsData) {
                    const assignmentsRef = collection(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots/${slot.id}/Assignments`);
                    const assignmentsSnap = await getDocs(assignmentsRef);
                    newAssignments[slot.id] = assignmentsSnap.docs.map(doc => ({
                        studentId: doc.id,
                        ...doc.data() as Omit<SlotAssignment, 'studentId'>
                    }));
                }
                setSlotAssignments(newAssignments);
                
                toast.success('Slot deleted successfully');
            } catch (error) {
                console.error('Error deleting slot:', error);
                toast.error('Failed to delete slot');
            }
        }
    };

    // Show error state
    if (slotsError) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-red-800">Error Loading Slots</h3>
                            <p className="text-red-700">{slotsError}</p>
                            <button
                                onClick={() => {
                                    // Refetch slots
                                    const fetchSlots = async () => {
                                        if (!providerId || !selectedSubscriptionId) return;
                                        setSlotsLoading(true);
                                        setSlotsError(null);
                                        try {
                                            const slotsRef = collection(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscriptionId}/Slots`);
                                            const slotsSnapshot = await getDocs(slotsRef);
                                            const slotsData: Slot[] = [];
                                            slotsSnapshot.forEach((doc) => {
                                                slotsData.push({
                                                    id: doc.id,
                                                    ...doc.data()
                                                } as Slot);
                                            });
                                            setSlots(slotsData);
                                        } catch (error) {
                                            console.error('Error fetching slots:', error);
                                            setSlotsError('Failed to load slots');
                                        } finally {
                                            setSlotsLoading(false);
                                        }
                                    };
                                    fetchSlots();
                                }}
                                className="mt-2 px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const renderSlotForm = (isEditing: boolean) => (
        <div className="space-y-4">
            {/* Name field */}
            <TextField
                label="Slot Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                size="small"
                fullWidth
                placeholder="e.g., Morning Batch A"
                error={!formData.name.trim()}
                helperText={!formData.name.trim() ? "Slot name is required" : ""}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '0.75rem',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgb(37 99 235)'
                        }
                    }
                }}
            />

            {/* Time and Duration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                    type="time"
                    label="Start Time"
                    value={formData.startTime}
                    onChange={(e) => handleInputChange('startTime', e.target.value)}
                    required
                    size="small"
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '0.75rem',
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgb(37 99 235)'
                            }
                        }
                    }}
                />

                <TextField
                    type="number"
                    label="Duration (minutes)"
                    value={formData.durationMinutes}
                    onChange={(e) => handleInputChange('durationMinutes', parseInt(e.target.value))}
                    required
                    size="small"
                    fullWidth
                    inputProps={{ min: 15, max: 480, step: 15 }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '0.75rem',
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgb(37 99 235)'
                            }
                        }
                    }}
                />
            </div>

            {/* Capacity */}
            <TextField
                type="number"
                label="Maximum Capacity"
                value={formData.maxCapacity}
                onChange={(e) => handleInputChange('maxCapacity', parseInt(e.target.value))}
                required
                size="small"
                fullWidth
                inputProps={{ min: 1, max: 100 }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '0.75rem',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgb(37 99 235)'
                        }
                    }
                }}
            />

            {/* Days Selection */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700">Days of Week</div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={handleSelectWeekdays}
                            className="text-sm text-blue-600 hover:text-blue-700"
                        >
                            Weekdays
                        </button>
                        <button
                            type="button"
                            onClick={handleSelectWeekends}
                            className="text-sm text-blue-600 hover:text-blue-700"
                        >
                            Weekends
                        </button>
                        <button
                            type="button"
                            onClick={handleClearAllDays}
                            className="text-sm text-blue-600 hover:text-blue-700"
                        >
                            Clear All
                        </button>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                        <button
                            key={day}
                            type="button"
                            onClick={() => handleDayChange(day)}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                formData.dayOfWeek.includes(day)
                                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {day.slice(0, 3)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Toggles */}
            <div className="flex items-center gap-4 pt-2">
                <FormControlLabel
                    control={
                        <Switch
                            checked={formData.isActive}
                            onChange={(e) => handleInputChange('isActive', e.target.checked)}
                            color="primary"
                        />
                    }
                    label={
                        <span className="text-sm font-medium text-gray-700">
                            Active Slot
                        </span>
                    }
                />
                <div className="flex items-center gap-1">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.markupAllowed}
                                onChange={(e) => handleInputChange('markupAllowed', e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <span className="text-sm font-medium text-gray-700">
                                Allow Markup
                            </span>
                        }
                    />
                    <Tooltip 
                        title="When enabled, customers outside this slot's regular schedule can book it"
                        placement="top"
                        arrow
                    >
                        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 cursor-help">
                            <span className="text-sm font-medium text-gray-600">?</span>
                        </div>
                    </Tooltip>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4">
                <button
                    type="button"
                    onClick={() => {
                        if (isEditing) {
                            setEditingSlot(null);
                        } else {
                            setShowAddForm(false);
                        }
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={isEditing ? handleUpdateSlot : handleCreateSlot}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                    {isEditing ? 'Update Slot' : 'Create Slot'}
                </button>
            </div>
        </div>
    );

    const renderSlotDisplay = (slot: Slot) => (
        <div className="flex items-center justify-between">
            <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                            {slot.name || 'Unnamed Slot'}
                        </span>
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                            slot.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                        }`}>
                            {slot.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {slot.markupAllowed && (
                            <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                Markup Allowed
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Assign Students Button */}
                        <button
                            onClick={() => {
                                setAssigningSlot(slot.id);
                                setIsAssignModalOpen(true);
                            }}
                            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Assign Students"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
                            </svg>
                        </button>
                        {/* Edit Button */}
                        <button
                            onClick={() => {
                                setEditingSlot(slot.id);
                                setFormData({
                                    name: slot.name || '',
                                    dayOfWeek: slot.dayOfWeek || [],
                                    startTime: slot.startTime || '09:00',
                                    durationMinutes: slot.durationMinutes || 60,
                                    maxCapacity: slot.maxCapacity || 10,
                                    isActive: typeof slot.isActive === 'boolean' ? slot.isActive : true,
                                    markupAllowed: typeof slot.markupAllowed === 'boolean' ? slot.markupAllowed : false
                                });
                            }}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit Slot"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                        </button>
                        {/* Delete Button */}
                        <button
                            onClick={() => handleDeleteSlot(slot.id)}
                            disabled={slotAssignments[slot.id]?.length > 0}
                            className={`p-1.5 rounded-lg transition-colors ${
                                slotAssignments[slot.id]?.length > 0
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : 'text-red-500 hover:text-red-700 hover:bg-red-50'
                            }`}
                            title={slotAssignments[slot.id]?.length > 0 ? 'Cannot delete slot with assigned students' : 'Delete Slot'}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="text-sm text-gray-600">
                    <div>{slot.dayOfWeek.join(', ')}</div>
                    <div className="mt-1">
                        <span>{formatTime(slot.startTime)} ({slot.durationMinutes} min)</span>
                        <span className="mx-2">•</span>
                        <span>Capacity: {(slotAssignments[slot.id] || []).length}/{slot.maxCapacity}</span>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="p-4 md:p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <h1 className="text-2xl font-bold text-gray-900">Manage Slots</h1>
                    <div className="relative">
                        <select
                            value={selectedSubscriptionId}
                            onChange={(e) => handleSubscriptionChange(e.target.value)}
                            className="block w-64 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cc_brand focus:border-cc_brand sm:text-sm rounded-md"
                        >
                            {allSubscriptions.map((subscription) => (
                                <option key={subscription.id} value={subscription.id}>
                                    {subscription.Name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="flex gap-2">
                    {!showAddForm && (
                        <button
                            onClick={() => setShowAddForm(true)}
                            className="px-4 py-2 bg-cc_brand text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                            Add New Slot
                        </button>
                    )}
                    <button
                        onClick={() => navigate('/mysubscriptions')}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                        Back to Subscriptions
                    </button>
                    <button
                        onClick={() => navigate(`/mysubscriptions/subscription/${selectedSubscriptionId}`)}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                        </svg>
                        View Subscription Details
                    </button>
                </div>
            </div>

            {/* Loading State */}
            {slotsLoading ? (
                <div className="flex justify-center items-center h-48">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cc_brand"></div>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Create New Slot Form */}
                    {showAddForm && (
                        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Create New Slot</h3>
                                <button
                                    onClick={() => setShowAddForm(false)}
                                    className="text-gray-400 hover:text-gray-500"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            {renderSlotForm(false)}
                        </div>
                    )}

                    {/* Slots List or Empty State */}
                    {slots.length === 0 && !showAddForm ? (
                        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
                            <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h3 className="mt-4 text-lg font-medium text-gray-900">No Slots Created</h3>
                            <p className="mt-2 text-sm text-gray-500">Get started by creating your first slot for this subscription.</p>
                            <div className="mt-6">
                                <button
                                    onClick={() => setShowAddForm(true)}
                                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cc_brand hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cc_brand"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                    </svg>
                                    Create New Slot
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {slots.map((slot) => (
                                <div key={slot.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                                    {editingSlot === slot.id ? (
                                        <div className="space-y-4">
                                            <div className="flex justify-between items-center mb-4">
                                                <h3 className="text-lg font-medium text-gray-900">Edit Slot</h3>
                                                <button
                                                    onClick={() => setEditingSlot(null)}
                                                    className="text-gray-400 hover:text-gray-500"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                            {renderSlotForm(true)}
                                        </div>
                                    ) : (
                                        <>
                                            {renderSlotDisplay(slot)}
                                            {/* Show assigned students */}
                                            {slotAssignments[slot.id]?.length > 0 && (
                                                <div className="mt-3 space-y-1">
                                                    {slotAssignments[slot.id].map((assignment) => (
                                                        <div key={assignment.studentId} 
                                                            className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-lg text-sm border border-blue-100"
                                                        >
                                                            <span className="font-medium text-blue-900">{assignment.studentName}</span>
                                                            <div className="flex items-center gap-2">
                                                                {/* Move student dropdown */}
                                                                <Menu as="div" className="relative inline-block text-left">
                                                                    <Menu.Button className="text-blue-700 hover:text-blue-900">
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                                                        </svg>
                                                                    </Menu.Button>
                                                                    <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                                                                        <div className="py-1">
                                                                            {slots.filter(s => s.id !== slot.id && s.isActive).map((targetSlot) => {
                                                                                const isTargetFull = (slotAssignments[targetSlot.id] || []).length >= targetSlot.maxCapacity;
                                                                                return (
                                                                                    <Menu.Item key={targetSlot.id}>
                                                                                        {({ active }) => (
                                                                                            <button
                                                                                                onClick={() => !isTargetFull && handleMoveStudent(assignment.studentId, slot.id, targetSlot.id)}
                                                                                                className={`${
                                                                                                    active ? 'bg-gray-100' : ''
                                                                                                } block w-full text-left px-4 py-2 text-sm ${
                                                                                                    isTargetFull ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'
                                                                                                }`}
                                                                                                disabled={isTargetFull}
                                                                                            >
                                                                                                Move to {targetSlot.name || 'Unnamed Slot'} ({formatTime(targetSlot.startTime)})
                                                                                                {isTargetFull && ' (Full)'}
                                                                                            </button>
                                                                                        )}
                                                                                    </Menu.Item>
                                                                                );
                                                                            })}
                                                                            <Menu.Item>
                                                                                {({ active }) => (
                                                                                    <button
                                                                                        onClick={() => handleRemoveStudent(assignment.studentId, slot.id)}
                                                                                        className={`${
                                                                                            active ? 'bg-red-50' : ''
                                                                                        } block w-full text-left px-4 py-2 text-sm text-red-600 hover:text-red-700`}
                                                                                    >
                                                                                        Remove from slot
                                                                                    </button>
                                                                                )}
                                                                            </Menu.Item>
                                                                        </div>
                                                                    </Menu.Items>
                                                                </Menu>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Assign Students Modal */}
            <Transition appear show={isAssignModalOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="fixed inset-0 z-10 overflow-y-auto"
                    onClose={() => {
                        setIsAssignModalOpen(false);
                        setSelectedStudents(new Set());
                        setAssigningSlot(null);
                    }}
                >
                    <div className="min-h-screen px-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-black bg-opacity-30" />
                        </Transition.Child>

                        {/* This element is to trick the browser into centering the modal contents. */}
                        <span
                            className="inline-block h-screen align-middle"
                            aria-hidden="true"
                        >
                            &#8203;
                        </span>

                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                                <Dialog.Title
                                    as="h3"
                                    className="text-lg font-medium leading-6 text-gray-900"
                                >
                                    Assign Students to Slot
                                </Dialog.Title>

                                <div className="mb-4">
                                    <input
                                        type="text"
                                        placeholder="Search students..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cc_brand"
                                    />
                                </div>

                                <div className="max-h-96 overflow-y-auto">
                                    {filteredLiveSubscriptions.length === 0 ? (
                                        <div className="text-center py-4 text-gray-500">
                                            No students found in this subscription
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {filteredLiveSubscriptions.map((student) => {
                                                const isAssigned = slotAssignments[assigningSlot || '']?.some(
                                                    assignment => assignment.studentId === student.id
                                                );
                                                const isSelected = selectedStudents.has(student.id);

                                                return (
                                                    <div
                                                        key={student.id}
                                                        className={`flex items-center justify-between p-3 rounded-lg border ${
                                                            isAssigned
                                                                ? 'bg-gray-50 border-gray-200'
                                                                : isSelected
                                                                    ? 'bg-blue-50 border-blue-200'
                                                                    : 'border-gray-200 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                disabled={isAssigned}
                                                                onChange={() => {
                                                                    const newSelected = new Set(selectedStudents);
                                                                    if (isSelected) {
                                                                        newSelected.delete(student.id);
                                                                    } else {
                                                                        newSelected.add(student.id);
                                                                    }
                                                                    setSelectedStudents(newSelected);
                                                                }}
                                                                className="h-4 w-4 text-cc_brand focus:ring-cc_brand border-gray-300 rounded"
                                                            />
                                                            <div>
                                                                <div className="font-medium">{student.studentName}</div>
                                                                <div className="text-sm text-gray-500">{student.consumerId}</div>
                                                            </div>
                                                        </div>
                                                        {isAssigned && (
                                                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                                                Already Assigned
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-6 flex justify-end gap-3">
                                    <button
                                        onClick={() => {
                                            setIsAssignModalOpen(false);
                                            setSelectedStudents(new Set());
                                            setAssigningSlot(null);
                                        }}
                                        className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleAssignStudents}
                                        disabled={selectedStudents.size === 0}
                                        className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 ${
                                            selectedStudents.size === 0
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : 'bg-cc_brand hover:bg-blue-600'
                                        } transition-colors`}
                                    >
                                        Assign {selectedStudents.size} Student{selectedStudents.size !== 1 ? 's' : ''}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
}